import React, { useCallback, useMemo, useState } from 'react';
import { SearchIcon } from 'lucide-react';
import { useSearchPage } from '@/shared/hooks/useSearchPage';
import { CustomModal } from '@/shared/components/ui/modal';
import { SearchModalContent } from '@/shared/components/ui/Search';
import ActionLinkButton from '@/shared/components/ui/ActionLinkButton';
import { useDisplayedCollectionStructure } from '@/features/collections/hooks/useDisplayedCollectionStructure';
import pageRepository from '@/features/pages/repositories/PageRepository';

interface SearchButtonProps {
  collectionId: string;
  showTitle?: boolean;
}

export default function CollectionSearchButton({
  collectionId,
  showTitle = true,
}: SearchButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  return (
    <>
      <ActionLinkButton
        Icon={SearchIcon}
        title={showTitle ? 'Search' : ''}
        onClick={() => {
          setIsModalOpen(true);
        }}
      />
      <CollectionSearchModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        collectionId={collectionId}
      />
    </>
  );
}

const CollectionSearchModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  collectionId: string;
}> = ({ isOpen, onClose, collectionId }) => {
  const { structure: collectionStructure } = useDisplayedCollectionStructure();
  const [includeFullText, setIncludeFullText] = useState(false);
  const currentCollectionPageMetadata = useMemo(
    () => collectionStructure.flatMap(section => section.section.pages),
    [collectionStructure]
  );
  const searchPages = useCallback(
    (query: string) => {
      if (includeFullText) {
        return pageRepository.searchPages(
          query,
          false,
          collectionId,
          true,
          currentCollectionPageMetadata.map(page => page.id)
        );
      }

      return currentCollectionPageMetadata.filter(p =>
        p.title.toLowerCase().includes(query.toLowerCase())
      );
    },
    [collectionId, currentCollectionPageMetadata, includeFullText]
  );
  const { searchQuery, searchResults, setSearchQuery, onClickPage } = useSearchPage({
    searchFn: searchPages,
    defaultResults: currentCollectionPageMetadata,
    collectionId,
  });

  const closeModal = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <CustomModal isOpen={isOpen} onClose={closeModal}>
      <SearchModalContent
        searchQuery={searchQuery}
        searchResults={searchResults}
        includeFullText={includeFullText}
        onSearchChange={setSearchQuery}
        onIncludeFullTextChange={setIncludeFullText}
        onClickPage={onClickPage}
      />
    </CustomModal>
  );
};
